import React from 'react';
import { Box, Text, Flex, useTheme } from '@chakra-ui/react';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';

export interface ChartTooltipEntry {
  dataKey: string;
  value: number;
  color?: string;
}

export interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipEntry[];
  label?: string | number;
  labelFormatter?: (label: string | number) => string;
  valueFormatter?: (value: number, dataKey: string) => string;
  getEntryLabel?: (dataKey: string) => string;
  getEntryColor?: (dataKey: string, theme: Record<string, unknown>) => string;
}

/**
 * Reusable chart tooltip component for Recharts charts.
 * Provides a consistent tooltip design with customizable formatting.
 */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
  getEntryLabel,
  getEntryColor,
}) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    const xAxisFormatter = useGetDateAxisFormatter();
    const formatLabel = labelFormatter || ((l) => xAxisFormatter(l || ''));

    return (
      <Box
        bg="surface.panel"
        border="1px solid"
        borderColor="surface.border"
        borderRadius="8px"
        p="12px"
        zIndex={1000}>
        <Text fontSize="14px" fontWeight="600" color="content.title" mb="8px">
          {formatLabel(label || '')}
        </Text>
        {payload
          .filter((entry, index, arr) => {
            return arr.findIndex((e) => e.dataKey === entry.dataKey) === index;
          })
          .map((entry, index: number) => {
            const color = getEntryColor
              ? getEntryColor(entry.dataKey, theme)
              : entry.color || theme.colors.chart.line.blue;
            const entryLabel = getEntryLabel
              ? getEntryLabel(entry.dataKey)
              : entry.dataKey;
            const formattedValue = valueFormatter
              ? valueFormatter(entry.value, entry.dataKey)
              : entry.value.toString();

            return (
              <Flex key={index} alignItems="center" mb="4px">
                <Box w="3" h="3" bg={color} borderRadius="sm" mr="8px" />
                <Text fontSize="12px" color="content.muted">
                  {entryLabel}:{' '}
                  <Text as="span" fontWeight="600" color={color}>
                    {formattedValue}
                  </Text>
                </Text>
              </Flex>
            );
          })}
      </Box>
    );
  }
  return null;
};
